if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityInputSticky = undefined;
      quantityFormSticky = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      unavailableAbortController = undefined;
      unavailableSelectionLock = 0;
      unavailableFetchGeneration = 0;
      freezePickerUpdates = false;
      activeUnavailablePickerInput = null;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input-primary');
        this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.suppressPickerChangeEvents = false;
        this.freezePickerUpdates = false;

        this.boundHandleUnavailableSwatchClick = this.handleUnavailableSwatchClick.bind(this);
        this.boundHandleUnavailableSwatchPointerDown = this.handleUnavailableSwatchPointerDown.bind(this);
        this.boundHandleCrossoutButtonPointerDown = this.handleCrossoutButtonPointerDown.bind(this);
        this.boundHandleCrossoutButtonClick = this.handleCrossoutButtonClick.bind(this);
        this.addEventListener('pointerdown', this.boundHandleUnavailableSwatchPointerDown, true);
        this.addEventListener('click', this.boundHandleUnavailableSwatchClick, true);
        this.addEventListener('pointerdown', this.boundHandleCrossoutButtonPointerDown, true);
        this.addEventListener('click', this.boundHandleCrossoutButtonClick, true);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput && !this.quantityInputSticky) return;

        this.quantityForm = this.querySelector('.product-form__quantity-primary');
        this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
        if (!this.quantityForm && !this.quantityFormSticky) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.removeEventListener('pointerdown', this.boundHandleUnavailableSwatchPointerDown, true);
        this.removeEventListener('click', this.boundHandleUnavailableSwatchClick, true);
        this.removeEventListener('pointerdown', this.boundHandleCrossoutButtonPointerDown, true);
        this.removeEventListener('click', this.boundHandleCrossoutButtonClick, true);
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (this.freezePickerUpdates || this.unavailableSelectionLock > 0) return;
        if (this.suppressPickerChangeEvents) return;

        const resolvedTarget = this.resolvePickerInput(target) || target;
        if (!this.contains(event.target) && !this.contains(resolvedTarget)) return;

        if (
          this.isUnavailablePickerInput(resolvedTarget) ||
          this.isUnavailablePickerInput(this.resolvePickerInput(event.target))
        ) {
          return;
        }

        if (this.isCrossoutButtonInput(resolvedTarget)) {
          return;
        }

        this.clearUnavailablePickerState();

        const savedScrollPosition = window.scrollY || window.pageYOffset;

        const productUrl = resolvedTarget.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url;
        const optionValueIds = (selectedOptionValues || []).filter(Boolean);

        const shouldSwapProduct = this.dataset.url !== productUrl;

        this.resetProductFormState();

        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValueIds, shouldFetchFullPage),
          targetId: resolvedTarget.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, optionValueIds),
        });

        this.restoreScrollPosition(savedScrollPosition);
      }

      restoreScrollPosition(savedScrollPosition) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (Math.abs(window.scrollY - savedScrollPosition) > 10) {
              window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
            }
          });
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          if (this.freezePickerUpdates || this.unavailableSelectionLock > 0) return;

          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        fetch(requestUrl, { signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (signal.aborted || this.freezePickerUpdates || this.unavailableSelectionLock > 0) return;

            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value (prevent scroll to avoid page jumping)
            const targetElement = document.querySelector(`#${targetId}`);
            if (targetElement) {
              targetElement.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      resolvePickerInput(target) {
        if (!target) return null;
        if (target.matches?.('input[type="radio"], select')) return target;

        if (target.tagName === 'LABEL' && target.htmlFor) {
          return document.getElementById(target.htmlFor);
        }

        const label = target.closest?.('label[for]');
        if (label?.htmlFor) {
          return document.getElementById(label.htmlFor);
        }

        return null;
      }

      isUnavailablePickerInput(input) {
        return Boolean(
          input?.hasAttribute?.('data-crossout') || input?.classList?.contains?.('visually-disabled')
        );
      }

      resolvePickerTarget(target) {
        if (!target) return null;
        if (target.isConnected) return target;
        return target.id ? document.getElementById(target.id) : null;
      }

      isCrossoutButtonInput(input) {
        return Boolean(
          input?.classList?.contains?.('button-input__input') && input?.hasAttribute?.('data-crossout')
        );
      }

      resolveUnavailableSwatchInput(event) {
        const variantSelects = event.target.closest?.('variant-selects[data-primary]');
        if (!variantSelects || !this.contains(variantSelects)) return null;

        const input = this.resolvePickerInput(event.target);
        if (!input || !variantSelects.contains(input) || input.type !== 'radio') return null;
        if (!input.classList.contains('swatch-input__input')) return null;

        return this.isUnavailablePickerInput(input) ? input : null;
      }

      resolveCrossoutButtonInput(event) {
        const variantSelects = event.target.closest?.('variant-selects[data-primary]');
        if (!variantSelects || !this.contains(variantSelects)) return null;

        const input = this.resolvePickerInput(event.target);
        if (!input || !variantSelects.contains(input)) return null;

        return this.isCrossoutButtonInput(input) ? input : null;
      }

      handleCrossoutButtonPointerDown(event) {
        if (event.button !== 0) return;

        const input = this.resolveCrossoutButtonInput(event);
        if (!input) return;

        event.preventDefault();
        event.stopPropagation();
      }

      handleCrossoutButtonClick(event) {
        const input = this.resolveCrossoutButtonInput(event);
        if (!input) return;

        event.preventDefault();
        event.stopPropagation();
      }

      clearUnavailablePickerState() {
        this.activeUnavailablePickerInput = null;

        this.variantSelectors?.querySelectorAll('.swatch-input__input.is-unavailable-active').forEach((input) => {
          input.classList.remove('is-unavailable-active');
        });

        this.variantSelectorsAlt?.querySelectorAll('.swatch-input__input.is-unavailable-active').forEach((input) => {
          input.classList.remove('is-unavailable-active');
        });
      }

      collectOptionValueIdsFromPicker() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return [];

        const ids = [];

        variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
          const id = input.getAttribute('data-option-value-id');
          if (id) ids.push(id);
        });

        variantSelects.querySelectorAll('.product-form__input--dropdown select').forEach((select) => {
          const option = select.options[select.selectedIndex];
          const id = option?.getAttribute('data-option-value-id');
          if (id) ids.push(id);
        });

        return ids;
      }

      beginUnavailableSelection() {
        this.abortController?.abort();
        this.pendingRequestUrl = null;
        this.freezePickerUpdates = true;

        if (this.unavailableSelectionLock === 0) {
          this.unavailableSelectionLock++;
        }
      }

      releaseUnavailableSelectionLock() {
        this.unavailableSelectionLock = Math.max(0, this.unavailableSelectionLock - 1);

        if (this.unavailableSelectionLock === 0) {
          this.freezePickerUpdates = false;
        }
      }

      handleUnavailableSwatchPointerDown(event) {
        if (event.button !== 0) return;

        const input = this.resolveUnavailableSwatchInput(event);
        if (!input) return;

        event.preventDefault();
        event.stopPropagation();

        this.beginUnavailableSelection();

        const optionValueIds = this.buildUnavailableOptionValueIds(input);
        this.applyUnavailablePickerSelection(input, optionValueIds);
      }

      handleUnavailableSwatchClick(event) {
        const input = this.resolveUnavailableSwatchInput(event);
        if (!input) return;

        event.preventDefault();
        event.stopPropagation();

        const productUrl = input.dataset.productUrl || this.dataset.url;
        const optionValueIds = this.buildUnavailableOptionValueIds(input);

        this.beginUnavailableSelection();

        if (this.activeUnavailablePickerInput !== input) {
          this.applyUnavailablePickerSelection(input, optionValueIds);
        }

        this.handleUnavailableOptionSelection(input, productUrl, optionValueIds);
      }

      buildUnavailableOptionValueIds(input) {
        const optionValueIds = this.collectOptionValueIdsFromPicker();
        const clickedOptionValueId = input.getAttribute('data-option-value-id');

        if (clickedOptionValueId) {
          const fieldset = input.closest('fieldset');
          const fieldsetOptionIds = fieldset
            ? Array.from(fieldset.querySelectorAll('input[type="radio"]'))
                .map((radio) => radio.getAttribute('data-option-value-id'))
                .filter(Boolean)
            : [];

          const withoutFieldsetIds = optionValueIds.filter((id) => !fieldsetOptionIds.includes(id));
          withoutFieldsetIds.push(clickedOptionValueId);
          return withoutFieldsetIds;
        }

        return optionValueIds;
      }

      selectUnavailablePickerInput(input) {
        const resolvedInput = this.resolvePickerTarget(input);
        if (!resolvedInput) return;

        this.activeUnavailablePickerInput = resolvedInput;

        this.runWithoutPickerChangeEvents(() => {
          const fieldset = resolvedInput.closest('fieldset');
          fieldset?.querySelectorAll('.swatch-input__input').forEach((radio) => {
            radio.classList.toggle('is-unavailable-active', radio === resolvedInput);
          });

          resolvedInput.checked = true;
          this.syncUnavailablePickerMetadata(resolvedInput);
        });
      }

      syncUnavailablePickerMetadata(input) {
        const fieldset = input.closest('.product-form__input');
        const selectedValueSpan = fieldset?.querySelector('[data-selected-value]');
        if (selectedValueSpan) selectedValueSpan.textContent = input.value;
      }

      handleUnavailableOptionSelection(target, productUrl, optionValueIds) {
        try {
          const variant = this.resolveSelectedVariantFromPicker();

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, { dispatchChange: false });

          if (!variant) {
            this.setUnavailable();
          } else {
            this.productForm?.toggleSubmitButton(
              !variant.available,
              !variant.available ? theme.variantStrings.soldOut : undefined
            );
          }

          this.fetchUnavailableOptionDetails(productUrl, optionValueIds, target);
        } catch (error) {
          this.releaseUnavailableSelectionLock();
          throw error;
        }
      }

      runWithoutPickerChangeEvents(callback) {
        const variantSelects = this.variantSelectors;
        const variantSelectsAlt = this.variantSelectorsAlt;
        this.suppressPickerChangeEvents = true;
        if (variantSelects) variantSelects.suppressChangePublish = true;
        if (variantSelectsAlt) variantSelectsAlt.suppressChangePublish = true;

        try {
          callback();
        } finally {
          queueMicrotask(() => {
            this.suppressPickerChangeEvents = false;
            if (variantSelects) variantSelects.suppressChangePublish = false;
            if (variantSelectsAlt) variantSelectsAlt.suppressChangePublish = false;
          });
        }
      }

      applyUnavailablePickerSelection(target, optionValueIds) {
        const resolvedTarget = this.resolvePickerTarget(target);
        if (!resolvedTarget) return;

        this.selectUnavailablePickerInput(resolvedTarget);
        this.markDependentOptionsFromVariants(resolvedTarget);

        const colorFieldset = resolvedTarget.closest('fieldset');
        const otherOptionValueIds = optionValueIds.filter((id) => {
          if (!colorFieldset) return true;
          return !colorFieldset.querySelector(`[data-option-value-id="${id}"]`);
        });

        if (!otherOptionValueIds.length) {
          this.reconcileUnavailableDependentOptions(resolvedTarget);
          return;
        }

        this.runWithoutPickerChangeEvents(() => {
          this.restoreSelectedOptionValues(otherOptionValueIds);
          this.markDependentOptionsFromVariants(resolvedTarget);
          this.reconcileUnavailableDependentOptions(resolvedTarget);
        });
      }

      getColorFieldsetIndex(colorInput, root) {
        const colorFieldset = colorInput?.closest('fieldset');
        if (!colorFieldset || !root) return -1;

        return Array.from(root.querySelectorAll('fieldset')).indexOf(colorFieldset);
      }

      setButtonInputCrossoutState(input, crossedOut) {
        if (!input?.classList?.contains('button-input__input')) return;

        if (crossedOut) {
          input.setAttribute('data-crossout', '');
          input.disabled = true;
          input.classList.add('disabled');
        } else {
          input.removeAttribute('data-crossout');
          input.disabled = false;
          input.classList.remove('disabled');
        }
      }

      markDependentOptionsFromVariants(unavailableColorInput) {
        const resolvedColorInput = this.resolvePickerTarget(unavailableColorInput);
        if (!resolvedColorInput) return;

        const variants = this.getProductVariants();
        if (!variants?.length) return;

        const colorOptionValueId = resolvedColorInput.getAttribute('data-option-value-id');
        const colorValue = resolvedColorInput.value;

        const markRoot = (root, colorInput) => {
          if (!root || !colorInput) return;

          const fieldsets = Array.from(root.querySelectorAll('fieldset'));
          const colorFieldsetIndex = this.getColorFieldsetIndex(colorInput, root);
          if (colorFieldsetIndex === -1) return;

          fieldsets.forEach((fieldset, fieldsetIndex) => {
            if (fieldsetIndex === colorFieldsetIndex) return;

            fieldset.querySelectorAll('input.button-input__input').forEach((input) => {
              const hypotheticalOptions = fieldsets.map((fs, index) => {
                if (index === colorFieldsetIndex) return colorValue;
                if (index === fieldsetIndex) return input.value;

                const checked = fs.querySelector('input[type="radio"]:checked');
                return checked?.value ?? null;
              });

              const matchingVariant = variants.find((variant) =>
                variant.options.every((option, index) => option === hypotheticalOptions[index])
              );

              this.setButtonInputCrossoutState(input, !matchingVariant?.available);
            });
          });
        };

        markRoot(this.variantSelectors, resolvedColorInput);

        const altColorInput = colorOptionValueId
          ? this.variantSelectorsAlt?.querySelector(`[data-option-value-id="${colorOptionValueId}"]`)
          : null;

        markRoot(this.variantSelectorsAlt, altColorInput ?? resolvedColorInput);
      }

      reconcileUnavailableDependentOptions(unavailableColorInput) {
        const resolvedColorInput = this.resolvePickerTarget(unavailableColorInput);
        if (!resolvedColorInput) return;

        const reconcileRoot = (root, colorInput) => {
          if (!root || !colorInput) return;

          const colorFieldsetIndex = this.getColorFieldsetIndex(colorInput, root);
          if (colorFieldsetIndex === -1) return;

          const fieldsets = Array.from(root.querySelectorAll('fieldset'));

          fieldsets.forEach((fieldset, fieldsetIndex) => {
            if (fieldsetIndex === colorFieldsetIndex) return;

            fieldset
              .querySelectorAll('input.button-input__input[data-crossout]:checked, input.button-input__input[disabled]:checked')
              .forEach((input) => {
                input.checked = false;
              });

            const checkedAvailable = fieldset.querySelector(
              'input.button-input__input:checked:not([data-crossout]):not([disabled])'
            );

            if (!checkedAvailable) {
              const firstAvailable = fieldset.querySelector(
                'input.button-input__input:not([data-crossout]):not([disabled])'
              );
              if (firstAvailable) {
                firstAvailable.checked = true;
                this.syncUnavailablePickerMetadata(firstAvailable);
              } else {
                const legendValue = fieldset.querySelector('[data-selected-value]');
                if (legendValue) legendValue.textContent = '';
              }
            }
          });
        };

        reconcileRoot(this.variantSelectors, resolvedColorInput);

        const colorOptionValueId = resolvedColorInput.getAttribute('data-option-value-id');
        const altColorInput = colorOptionValueId
          ? this.variantSelectorsAlt?.querySelector(
              `[data-option-value-id="${colorOptionValueId}"]`
            )
          : null;

        reconcileRoot(this.variantSelectorsAlt, altColorInput ?? resolvedColorInput);
      }

      syncUnavailablePickerFromResponse(html, target, optionValueIds) {
        this.updateOptionValues(html, { force: true });
        this.applyUnavailablePickerSelection(target, optionValueIds);
      }

      preserveUnavailablePickerSelection(target, optionValueIds, onComplete) {
        requestAnimationFrame(() => {
          this.applyUnavailablePickerSelection(target, optionValueIds);
          onComplete?.();
        });
      }

      safeSetActiveMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return false;

        const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!slide) return false;

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery?.setActiveMedia) {
          mediaGallery.setActiveMedia(mediaId, true, false);
          return true;
        }

        const productGallery = this.querySelector('product-gallery');
        if (productGallery) {
          productGallery.querySelectorAll('[data-media-id]').forEach((item) => {
            item.classList.toggle('is-active', item === slide);
          });
          return true;
        }

        return false;
      }

      fetchUnavailableOptionDetails(productUrl, optionValueIds, target) {
        const targetId = target?.id;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, optionValueIds);
        const fetchGeneration = ++this.unavailableFetchGeneration;

        this.unavailableAbortController?.abort();
        this.unavailableAbortController = new AbortController();

        fetch(requestUrl, { signal: this.unavailableAbortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (fetchGeneration !== this.unavailableFetchGeneration) return;

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const variant = this.resolveSelectedVariantFromPicker() ?? this.getSelectedVariant(html);

            if (variant?.featured_media?.id) {
              this.updateMedia(html, variant.featured_media.id);
            }

            this.syncUnavailablePickerFromResponse(html, target, optionValueIds);

            if (!variant) {
              this.setUnavailable();
              this.preserveUnavailablePickerSelection(target, optionValueIds, () => {
                if (fetchGeneration === this.unavailableFetchGeneration) {
                  this.releaseUnavailableSelectionLock();
                }
              });
              return;
            }

            this.updateUnavailableOptionPriceBlocks(html, variant);

            this.preserveUnavailablePickerSelection(target, optionValueIds, () => {
              if (fetchGeneration !== this.unavailableFetchGeneration) return;

              this.releaseUnavailableSelectionLock();

              publish(PUB_SUB_EVENTS.variantChange, {
                data: {
                  sectionId: this.sectionId,
                  variant,
                },
              });
            });
          })
          .then(() => {
            if (fetchGeneration !== this.unavailableFetchGeneration) return;

            const targetElement = targetId ? document.getElementById(targetId) : null;
            targetElement?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;

            console.error(error);

            if (fetchGeneration === this.unavailableFetchGeneration) {
              this.releaseUnavailableSelectionLock();
            }
          });
      }

      updateUnavailableOptionPriceBlocks(html, variant) {
        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Price-Alt');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

        this.productForm?.toggleSubmitButton(
          !variant.available,
          !variant.available ? theme.variantStrings.soldOut : undefined
        );
      }

      getProductVariants() {
        const script = this.variantSelectors?.querySelector('[data-product-variants]');
        if (!script?.textContent) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      resolveSelectedVariantFromPicker() {
        const variants = this.getProductVariants();
        const variantSelects = this.variantSelectors;
        if (!variants?.length || !variantSelects) return null;

        const selectedOptions = [];

        variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) selectedOptions.push(checked.value);
        });

        variantSelects.querySelectorAll('.product-form__input--dropdown select').forEach((select) => {
          selectedOptions.push(select.value);
        });

        return (
          variants.find((variant) =>
            variant.options.every((option, index) => option === selectedOptions[index])
          ) ?? null
        );
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html, { force = false } = {}) {
        if (!force && (this.freezePickerUpdates || this.unavailableSelectionLock > 0)) return;

        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      restoreSelectedOptionValues(selectedOptionValues) {
        if (!selectedOptionValues?.length) return;

        const applySelection = (root) => {
          if (!root) return;

          selectedOptionValues.forEach((optionValueId) => {
            root.querySelectorAll(`[data-option-value-id="${optionValueId}"]`).forEach((element) => {
              if (element.tagName === 'INPUT' && element.type === 'radio') {
                element.checked = true;
                this.syncUnavailablePickerMetadata(element);
              } else if (element.tagName === 'OPTION') {
                element.selected = true;
                const select = element.closest('select');
                if (select) select.value = element.value;
              }
            });
          });
        };

        const applyToPickers = () => {
          applySelection(this.variantSelectors);
          applySelection(this.variantSelectorsAlt);
        };

        if (this.suppressPickerChangeEvents) {
          applyToPickers();
          return;
        }

        this.runWithoutPickerChangeEvents(applyToPickers);
      }

      preservePickerSelection(selectedOptionValues) {
        this.restoreSelectedOptionValues(selectedOptionValues);

        requestAnimationFrame(() => {
          this.restoreSelectedOptionValues(selectedOptionValues);
        });
      }

      handleUpdateProductInfo(productUrl, selectedOptionValues = []) {
        return (html) => {
          if (this.freezePickerUpdates || this.unavailableSelectionLock > 0) return;

          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.restoreSelectedOptionValues(selectedOptionValues);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateCustomLiquid(html);
          
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            theme.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateCustomLiquid(html) {
        this.querySelectorAll('.custom__liquid').forEach(block => {
          if (block.id) {
            const liquid = html.querySelector(`#${block.id}`);
            if (liquid) block.innerHTML = liquid.innerHTML;
          }
        });
      }

      updateVariantInputs(variantId, { dispatchChange = true } = {}) {
        // Save scroll position before updating variant inputs
        const savedScrollPosition = window.scrollY || window.pageYOffset;
        
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          if (dispatchChange) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        
        // Restore scroll position after variant input update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (Math.abs(window.scrollY - savedScrollPosition) > 10) {
              window.scrollTo({ top: savedScrollPosition, behavior: 'instant' });
            }
          });
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

        const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const thumbnailsD5 = document.querySelector('.product-thumbnails-d5 ul');
        const thumbnailsNewD5 = html.querySelector('.product-thumbnails-d5 ul');
        if(thumbnailsNewD5 && thumbnailsD5){
          thumbnailsD5.innerHTML = thumbnailsNewD5.innerHTML;
          
           window.reinitThumbnailClicks();

          //document.querySelector('thumbnail-slider .slider-button.slider-button--next').removeAttribute('disabled');
        }
        const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        // Prevent scroll when variant changes programmatically (not user-initiated)
        if (!this.safeSetActiveMedia(variantFeaturedMediaId)) {
          this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const input = this.quantityInput || this.quantityInputSticky;
        const data = {
          cartQuantity: input?.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
          min: input?.dataset.min ? parseInt(input.dataset.min) : 1,
          max: input?.dataset.max ? parseInt(input.dataset.max) : null,
          step: input?.step ? parseInt(input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        if(this.quantityInput) this.quantityInput.min = min;
        if(this.quantityInputSticky) this.quantityInputSticky.min = min;

        if (max) {
          if(this.quantityInput) this.quantityInput.max = max;
          if(this.quantityInputSticky) this.quantityInputSticky.max = max;
        } else {
          this.quantityInput?.removeAttribute('max');
          this.quantityInputSticky?.removeAttribute('max');
        }
        if(this.quantityInput) this.quantityInput.value = min;
        if(this.quantityInputSticky) this.quantityInputSticky.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        //this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            //this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput && !this.quantityInputSticky) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const currentSticky = this.quantityFormSticky?.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if(!updated) continue;
          if (!current && !currentSticky) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current?.setAttribute(attribute, valueUpdated);
                currentSticky?.setAttribute(attribute, valueUpdated);
              } else {
                current?.removeAttribute(attribute);
                currentSticky?.removeAttribute(attribute);
              }
            }
          } else {
            if(current) current.innerHTML = updated.innerHTML;
            if(currentSticky) currentSticky.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects[data-primary]');
      }

      get variantSelectorsAlt() {
        return this.querySelector('variant-selects[data-sticky]');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
